import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { DateInput } from '@/components/ui/DateInput';
import { Input } from '@/components/ui/Input';
import toast from 'react-hot-toast';
import { vendorService, Vendor } from '../services/vendor.service';

const inputClassName =
  'w-full rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500';

const CONTRACT_TYPES = [
  'MSA',
  'SOW',
  'NDA',
  'SLA',
  'SOFTWARE_LICENSE',
  'LEASE',
  'AMENDMENT',
  'PURCHASE_ORDER',
  'CONSULTING',
  'OTHER',
] as const;

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF'] as const;

const PAYMENT_TERMS = [
  'NET_30',
  'NET_45',
  'NET_60',
  'NET_90',
  'UPON_DELIVERY',
  'MONTHLY',
  'QUARTERLY',
  'ANNUALLY',
  'CUSTOM',
] as const;

const PAYMENT_FREQUENCIES = [
  'ONE_TIME',
  'MONTHLY',
  'QUARTERLY',
  'SEMI_ANNUAL',
  'ANNUAL',
] as const;

const CONFIDENTIALITY_LEVELS = [
  'PUBLIC',
  'INTERNAL',
  'CONFIDENTIAL',
  'HIGHLY_CONFIDENTIAL',
] as const;

interface FormData {
  title: string;
  contractType: string;
  vendorId: string;
  contractValue: string;
  currency: string;
  paymentTerms: string;
  paymentFrequency: string;
  annualValue: string;
  budgetCode: string;
  effectiveDate: string;
  expirationDate: string;
  executionDate: string;
  nextReviewDate: string;
  isAutoRenewal: boolean;
  noticePeriodDays: string;
  autoRenewalTermMonths: string;
  maxRenewalCount: string;
  governingLaw: string;
  confidentialityLevel: string;
  description: string;
  internalNotes: string;
}

export default function CreateContract() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledVendorId = searchParams.get('vendorId') ?? '';

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    contractType: 'MSA',
    vendorId: prefilledVendorId,
    contractValue: '',
    currency: 'USD',
    paymentTerms: '',
    paymentFrequency: '',
    annualValue: '',
    budgetCode: '',
    effectiveDate: '',
    expirationDate: '',
    executionDate: '',
    nextReviewDate: '',
    isAutoRenewal: false,
    noticePeriodDays: '',
    autoRenewalTermMonths: '',
    maxRenewalCount: '',
    governingLaw: '',
    confidentialityLevel: 'INTERNAL',
    description: '',
    internalNotes: '',
  });

  useEffect(() => {
    vendorService
      .getVendors({ limit: 100 })
      .then((res) => setVendors(res.vendors))
      .catch((err) => {
        console.error('Failed to load vendors:', err);
        toast.error('Failed to load vendor list');
      });
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.vendorId) {
      toast.error('Vendor is required');
      return;
    }

    try {
      setSubmitting(true);
      const payload: Record<string, unknown> = {
        title: formData.title.trim(),
        contractType: formData.contractType,
        vendorId: formData.vendorId,
        currency: formData.currency,
        confidentialityLevel: formData.confidentialityLevel,
        isAutoRenewal: formData.isAutoRenewal,
      };

      if (formData.contractValue) {payload.contractValue = parseFloat(formData.contractValue);}
      if (formData.paymentTerms) {payload.paymentTerms = formData.paymentTerms;}
      if (formData.paymentFrequency) {payload.paymentFrequency = formData.paymentFrequency;}
      if (formData.annualValue) {payload.annualValue = parseFloat(formData.annualValue);}
      if (formData.budgetCode.trim()) {payload.budgetCode = formData.budgetCode.trim();}
      if (formData.effectiveDate) {payload.effectiveDate = formData.effectiveDate;}
      if (formData.expirationDate) {payload.expirationDate = formData.expirationDate;}
      if (formData.executionDate) {payload.executionDate = formData.executionDate;}
      if (formData.nextReviewDate) {payload.nextReviewDate = formData.nextReviewDate;}
      if (formData.noticePeriodDays) {payload.noticePeriodDays = parseInt(formData.noticePeriodDays, 10);}
      if (formData.isAutoRenewal && formData.autoRenewalTermMonths)
        {payload.autoRenewalTermMonths = parseInt(formData.autoRenewalTermMonths, 10);}
      if (formData.isAutoRenewal && formData.maxRenewalCount)
        {payload.maxRenewalCount = parseInt(formData.maxRenewalCount, 10);}
      if (formData.governingLaw.trim()) {payload.governingLaw = formData.governingLaw.trim();}
      if (formData.description.trim()) {payload.description = formData.description.trim();}
      if (formData.internalNotes.trim()) {payload.internalNotes = formData.internalNotes.trim();}

      const contract = await vendorService.createContract(payload);
      toast.success('Contract created successfully');
      navigate(`/legal/contracts/${contract.id}`);
    } catch (err) {
      console.error('Failed to create contract:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create contract');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
            Create New Contract
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400 mt-1">
            Fill in the details below to create a new contract
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Title <span className="text-danger-500">*</span>
                </label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter contract title"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Contract Type
                </label>
                <select
                  name="contractType"
                  value={formData.contractType}
                  onChange={handleChange}
                  className={inputClassName}
                >
                  {CONTRACT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {vendorService.getContractTypeLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Vendor <span className="text-danger-500">*</span>
                </label>
                <select
                  name="vendorId"
                  value={formData.vendorId}
                  onChange={handleChange}
                  className={inputClassName}
                  required
                >
                  <option value="">Select a vendor</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.companyLegalName} ({v.vendorNumber})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial Terms */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
              Financial Terms
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Contract Value
                </label>
                <input
                  type="number"
                  name="contractValue"
                  value={formData.contractValue}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className={inputClassName}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Currency
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className={inputClassName}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Payment Terms
                </label>
                <select
                  name="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={handleChange}
                  className={inputClassName}
                >
                  <option value="">Select payment terms</option>
                  {PAYMENT_TERMS.map((pt) => (
                    <option key={pt} value={pt}>
                      {pt.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Payment Frequency
                </label>
                <select
                  name="paymentFrequency"
                  value={formData.paymentFrequency}
                  onChange={handleChange}
                  className={inputClassName}
                >
                  <option value="">Select frequency</option>
                  {PAYMENT_FREQUENCIES.map((pf) => (
                    <option key={pf} value={pf}>
                      {pf.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Annual Value
                </label>
                <input
                  type="number"
                  name="annualValue"
                  value={formData.annualValue}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className={inputClassName}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Budget Code
                </label>
                <Input
                  name="budgetCode"
                  value={formData.budgetCode}
                  onChange={handleChange}
                  placeholder="Enter budget code"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Dates */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
              Key Dates
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <DateInput
                  label="Effective Date"
                  value={formData.effectiveDate}
                  onChange={(value) => setFormData((prev) => ({ ...prev, effectiveDate: value }))}
                />
              </div>
              <div>
                <DateInput
                  label="Expiration Date"
                  value={formData.expirationDate}
                  onChange={(value) => setFormData((prev) => ({ ...prev, expirationDate: value }))}
                />
              </div>
              <div>
                <DateInput
                  label="Execution Date"
                  value={formData.executionDate}
                  onChange={(value) => setFormData((prev) => ({ ...prev, executionDate: value }))}
                />
              </div>
              <div>
                <DateInput
                  label="Next Review Date"
                  value={formData.nextReviewDate}
                  onChange={(value) => setFormData((prev) => ({ ...prev, nextReviewDate: value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Renewal Terms */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
              Renewal Terms
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isAutoRenewal"
                    checked={formData.isAutoRenewal}
                    onChange={handleChange}
                    className="rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                    Auto-Renewal
                  </span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Notice Period (days)
                </label>
                <input
                  type="number"
                  name="noticePeriodDays"
                  value={formData.noticePeriodDays}
                  onChange={handleChange}
                  placeholder="e.g. 30"
                  min="0"
                  className={inputClassName}
                />
              </div>
              {formData.isAutoRenewal && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Auto-Renewal Term (months)
                    </label>
                    <input
                      type="number"
                      name="autoRenewalTermMonths"
                      value={formData.autoRenewalTermMonths}
                      onChange={handleChange}
                      placeholder="e.g. 12"
                      min="1"
                      className={inputClassName}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Max Renewal Count
                    </label>
                    <input
                      type="number"
                      name="maxRenewalCount"
                      value={formData.maxRenewalCount}
                      onChange={handleChange}
                      placeholder="e.g. 3"
                      min="0"
                      className={inputClassName}
                    />
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Additional */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
              Additional
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Governing Law
                </label>
                <Input
                  name="governingLaw"
                  value={formData.governingLaw}
                  onChange={handleChange}
                  placeholder="e.g. State of Delaware"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Confidentiality Level
                </label>
                <select
                  name="confidentialityLevel"
                  value={formData.confidentialityLevel}
                  onChange={handleChange}
                  className={inputClassName}
                >
                  {CONFIDENTIALITY_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {level.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Enter contract description..."
                  className={inputClassName}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Internal Notes
                </label>
                <textarea
                  name="internalNotes"
                  value={formData.internalNotes}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Internal notes (not visible to vendor)..."
                  className={inputClassName}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Contract'}
          </Button>
        </div>
      </form>
    </div>
  );
}
